import {
	Component,
	computed,
	ElementRef,
	OnDestroy,
	ViewChild,
	input,
	output,
	signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { StripeElements, StripePaymentElement } from '@stripe/stripe-js';
import { BootstrapService } from '../../core/services/bootstrap.service';
import { StripeService } from '../../core/services/stripe/stripe.service';
import { PaymentService } from '../../core/services/payment/payment.service';
import { UserService } from '../../core/services/user.service';
import { ModalActions, ModalTypes } from '../../core/enums/base.enums';
import { CurrencyDisplayPipe } from '../../core/pipes/currency/currency.pipe';
import { PaymentMethodType, PaymentStatus } from '../../core/enums/data.enums';
import { PaymentMethodModel } from '../../core/models/service/payment-method.model';
import { CARD_BRAND_DISPLAY_LABELS, CARD_BRAND_ICONS } from '../../core/constants/display-labels.constants';
import { GenericMessagingConstants, PaymentMessagingConstants, StripeMessagingConstants } from '../../core/constants/messaging.constants';
import { EscCloseDirective } from '../../core/directives/esc-close.directive';
import { TooltipDirective } from '../../core/directives/tooltip.directive';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { BulkActionRequest } from '../../core/models/service/bulk-action-request.model';
import { PropertyChargeFilter } from '../../core/filters/property-charge.filter';
import {
	PaymentIntentModel,
	PaymentPreviewModel
} from '../../core/models/data/payment.model';
import { StripeAddCreditCardComponent } from '../../features/stripe/components/stripe-add-credit-card/stripe-add-credit-card.component';
import { BaseFormComponent } from '../../core/base-classes/base-form-component';
import { CanComponentDeactivate } from '../../core/routing/can-deactivate.guard';
import { DirtyFormRegistryService } from '../../core/services/dirty-form-registry.service';

enum ModalStep {
	SelectMethod = 'selectMethod',
	SelectType = 'selectType',
	AddCard = 'addCard',
	OneTimePayForm = 'oneTimePayForm',
	Preview = 'preview',
	Processing = 'processing',
	Confirmation = 'confirmation',
	Error = 'error'
}

type ErrorContext = 'preview' | 'confirm';

@Component({
	selector: 'ms-payment-modal',
	standalone: true,
	imports: [CommonModule, FontAwesomeModule, CurrencyDisplayPipe, StripeAddCreditCardComponent, EscCloseDirective, TooltipDirective, ConfirmationModalComponent],
	templateUrl: './payment-modal.component.html',
	styleUrl: './payment-modal.component.scss'
})
export class PaymentModalComponent extends BaseFormComponent implements OnDestroy, CanComponentDeactivate {

	// #region Properties

	@ViewChild('addCreditCardComponent') addCreditCardComponent?: StripeAddCreditCardComponent;
	@ViewChild('paymentElementContainer') paymentElementContainer?: ElementRef<HTMLDivElement>;
	@ViewChild('escConfirmationModalRef') escConfirmationModalRef!: ConfirmationModalComponent;

	public userId = input.required<number>();
	public organizationId = input.required<number>();

	public paymentCompleted = output<PaymentIntentModel>();
	public cancelled = output<void>();

	public currentStep = signal<ModalStep>(ModalStep.SelectMethod);
	public paymentMethods = signal<PaymentMethodModel[]>([]);
	public selectedMethod = signal<PaymentMethodModel | null>(null);
	public isOneTimeFlow = signal(false);
	public isLoading = signal(false);
	public preview = signal<PaymentPreviewModel | null>(null);
	public paymentIntent = signal<PaymentIntentModel | null>(null);
	public isLoadingPreview = signal(false);
	public isPaymentElementReady = signal(false);
	public errorMessage = signal<string>('');

	public isPaymentFlowDirty = computed(() => {
		const step = this.currentStep();
		return step === ModalStep.AddCard
			|| step === ModalStep.OneTimePayForm
			|| step === ModalStep.Preview
			|| step === ModalStep.Processing;
	});

	public hasItemLateFees = computed(() => this.preview()?.items?.some(i => i.lateFees > 0) ?? false);

	public ModalStep = ModalStep;
	public ModalTypes = ModalTypes;
	public PaymentStatus = PaymentStatus;
	public PaymentMethodType = PaymentMethodType;
	public PaymentMessagingConstants = PaymentMessagingConstants;
	public StripeMessagingConstants = StripeMessagingConstants;

	private stripeCustomerAccountId: string = '';
	private oneTimePaymentMethodType: PaymentMethodType = PaymentMethodType.Card;
	private charges: BulkActionRequest<PropertyChargeFilter> | null = null;
	private elements: StripeElements | null = null;
	private paymentElement: StripePaymentElement | null = null;
	private errorContext: ErrorContext = 'preview';
	private selectedSavedMethod: PaymentMethodModel | null = null;
	private deferredCurrencyCode: string = 'usd';
	private deferredAmount: number = 100;

	private readonly MODAL_ID = 'paymentModal';

	// #endregion

	constructor(
		private bootstrapService: BootstrapService,
		private stripeService: StripeService,
		private paymentService: PaymentService,
		private userService: UserService,
		private router: Router,
		private dirtyFormRegistry: DirtyFormRegistryService
	) {
		super();
	}

	ngOnDestroy(): void {
		this.dirtyFormRegistry.unregister(this);
		this.cleanupStripeElements();
	}

	// #region Public Methods

	public async open(charges: BulkActionRequest<PropertyChargeFilter>, currencyCode?: string, estimatedAmount?: number): Promise<void> {
		this.charges = charges;
		this.deferredCurrencyCode = currencyCode?.toLowerCase() || 'usd';
		this.deferredAmount = estimatedAmount || 100;

		this.currentStep.set(ModalStep.SelectMethod);
		this.selectedMethod.set(null);
		this.isOneTimeFlow.set(false);
		this.preview.set(null);
		this.paymentIntent.set(null);
		this.errorMessage.set('');
		this.isLoadingPreview.set(false);
		this.isPaymentElementReady.set(false);
		this.selectedSavedMethod = null;
		this.cleanupStripeElements();

		this.dirtyFormRegistry.register(this);
		this.bootstrapService.manageModal(`#${this.MODAL_ID}`, ModalActions.Show);
		await this.loadPaymentMethods();
	}

	public selectMethod(method: PaymentMethodModel): void {
		this.selectedMethod.set(method);
	}


	public async payWithSelectedMethod(): Promise<void> {
		const method = this.selectedMethod();
		if (!method) return;

		this.selectedSavedMethod = method;
		this.currentStep.set(ModalStep.Preview);
		await this.loadPreview();
	}

	public showAddNewStep(): void {
		this.currentStep.set(ModalStep.SelectType);
	}

	public addAndSaveCard(): void {
		this.currentStep.set(ModalStep.AddCard);
		setTimeout(async () => {
			if (this.addCreditCardComponent) {
				await this.addCreditCardComponent.initialize();
			}
		}, 0);
	}

	public async payOnceWithCard(): Promise<void> {
		this.oneTimePaymentMethodType = PaymentMethodType.Card;
		this.isOneTimeFlow.set(true);
		this.currentStep.set(ModalStep.OneTimePayForm);
		await this.mountDeferredPaymentElement();
	}

	public async onPaymentMethodSaved(): Promise<void> {
		this.currentStep.set(ModalStep.SelectMethod);
		await this.loadPaymentMethods();

		const methods = this.paymentMethods();
		if (methods.length > 0) {
			this.selectedMethod.set(methods[0]);
		}
	}

	public onAddFormCancelled(): void {
		this.currentStep.set(ModalStep.SelectType);
	}

	public goToPaymentSettings(): void {
		this.close();
		this.router.navigate(['/users/profile', this.userId()], { queryParams: { tab: 'settings' } });
	}

	public async continueToPreview(): Promise<void> {
		if (this.elements) {
			const result = await this.elements.submit();
			if (result.error) {
				return;
			}
		}

		this.currentStep.set(ModalStep.Preview);
		await this.loadPreview();
	}

	public async goBack(): Promise<void> {
		const step = this.currentStep();

		switch (step) {
			case ModalStep.SelectMethod:
				this.close();
				this.cancelled.emit();
				return;

			case ModalStep.SelectType:
				this.isOneTimeFlow.set(false);
				this.currentStep.set(ModalStep.SelectMethod);
				return;

			case ModalStep.OneTimePayForm:
				this.cleanupStripeElements();
				this.currentStep.set(ModalStep.SelectType);
				return;

			case ModalStep.Preview:
				if (this.selectedSavedMethod) {
					this.currentStep.set(ModalStep.SelectMethod);
				} else {
					// One-time card: element is still mounted (hidden via CSS), just show it
					this.currentStep.set(ModalStep.OneTimePayForm);
				}
				return;

			case ModalStep.Error:
				this.goBackFromError();
				return;
		}
	}

	public async confirmPayment(): Promise<void> {
		if (this.selectedSavedMethod) {
			await this.confirmSavedMethodPayment();
		} else {
			await this.confirmOneTimeCardPayment();
		}
	}

	public async retryPayment(): Promise<void> {
		this.errorMessage.set('');

		switch (this.errorContext) {
			case 'preview':
				this.currentStep.set(ModalStep.Preview);
				await this.loadPreview();
				return;

			case 'confirm':
				this.currentStep.set(ModalStep.Preview);
				return;
		}
	}

	public onDone(): void {
		const intent = this.paymentIntent();
		if (intent) {
			this.paymentCompleted.emit(intent);
		}
		this.close();
	}

	public onCancel(): void {
		if (this.currentStep() === ModalStep.Processing) return;

		if (this.isPaymentFlowDirty()) {
			this.escConfirmationModalRef.modalType.set(ModalTypes.Question);
			this.escConfirmationModalRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
			this.bootstrapService.manageModal('#paymentEscConfirmationModal', ModalActions.Show);
		} else {
			this.close();
			this.cancelled.emit();
		}
	}

	public canDeactivate(): boolean | Promise<boolean> {
		return this.awaitLeaveConfirmation(this.isPaymentFlowDirty(), () => {
			this.escConfirmationModalRef.modalType.set(ModalTypes.Question);
			this.escConfirmationModalRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
			this.bootstrapService.manageModal('#paymentEscConfirmationModal', ModalActions.Show);
		});
	}

	public onEscConfirmed(): void {
		this.bootstrapService.manageModal('#paymentEscConfirmationModal', ModalActions.Hide);
		this.close();
		this.cancelled.emit();
		this.resolvePendingLeave(true);
	}

	public onEscCancelled(): void {
		this.bootstrapService.manageModal('#paymentEscConfirmationModal', ModalActions.Hide);
		this.resolvePendingLeave(false);
	}

	// #endregion

	// #region Display Helpers

	public isLargeStep(): boolean {
		const step = this.currentStep();
		return step === ModalStep.SelectMethod
			|| step === ModalStep.SelectType
			|| step === ModalStep.AddCard
			|| step === ModalStep.OneTimePayForm
			|| step === ModalStep.Preview;
	}

	public getMethodDisplayName(method: PaymentMethodModel): string {
		if (method.paymentMethodType === PaymentMethodType.Card && method.card) {
			const brandName = CARD_BRAND_DISPLAY_LABELS[method.card.brand] ?? PaymentMethodType.Card;
			return `${brandName} ending in ${method.card.last4}`;
		}
		if (method.paymentMethodType === PaymentMethodType.AchDebit && method.usBankAccount) {
			return `${method.usBankAccount.bankName} ending in ${method.usBankAccount.last4}`;
		}
		return 'Unknown payment method';
	}

	public getMethodExpiry(method: PaymentMethodModel): string {
		if (method.paymentMethodType === PaymentMethodType.Card && method.card) {
			return `${String(method.card.expMonth).padStart(2, '0')}/${method.card.expYear}`;
		}
		return '';
	}

	public getMethodIcon(method: PaymentMethodModel): IconProp {
		if (method.paymentMethodType === PaymentMethodType.Card && method.card) {
			return CARD_BRAND_ICONS[method.card.brand] ?? ['fad', 'credit-card'];
		}
		if (method.paymentMethodType === PaymentMethodType.AchDebit) {
			return ['fad', 'building-columns'];
		}
		return ['fad', 'credit-card'];
	}

	public getActivePaymentDisplayName(): string {
		if (this.selectedSavedMethod) {
			return this.getMethodDisplayName(this.selectedSavedMethod);
		}
		return this.oneTimePaymentMethodType === PaymentMethodType.AchDebit
			? PaymentMessagingConstants.PAYMENT_ONE_TIME_ACH_LABEL()
			: PaymentMessagingConstants.PAYMENT_ONE_TIME_CARD_LABEL();
	}

	public getActivePaymentIcon(): IconProp {
		if (this.selectedSavedMethod) {
			return this.getMethodIcon(this.selectedSavedMethod);
		}
		return this.oneTimePaymentMethodType === PaymentMethodType.AchDebit
			? ['fad', 'building-columns']
			: ['fad', 'credit-card'];
	}

	public getConfirmationMessage(): string {
		const intent = this.paymentIntent();
		const previewData = this.preview();
		if (!intent || !previewData) return '';

		const amount = new CurrencyDisplayPipe().transform(intent.totalAmount, previewData.currency);

		const isAch = this.selectedSavedMethod
			? this.selectedSavedMethod.paymentMethodType === PaymentMethodType.AchDebit
			: this.oneTimePaymentMethodType === PaymentMethodType.AchDebit;

		if (isAch) {
			return PaymentMessagingConstants.PAYMENT_SUCCESS_ACH().replace('{amount}', amount);
		}
		return PaymentMessagingConstants.PAYMENT_SUCCESS_CARD().replace('{amount}', amount);
	}

	public getStatusBadgeClass(): string {
		const status = this.paymentIntent()?.status;
		if (status === PaymentStatus.Succeeded) return 'bg-success-subtle text-success-emphasis';
		if (status === PaymentStatus.Pending) return 'bg-info-subtle text-info-emphasis';
		return 'bg-secondary-subtle text-secondary-emphasis';
	}

	// #endregion

	// #region Private Methods

	private async loadPaymentMethods(): Promise<void> {
		this.isLoading.set(true);
		this.errorMessage.set('');

		try {
			const result = await this.userService.getPaymentMethods(this.userId());

			if (result.success && result.data) {
				this.stripeCustomerAccountId = result.data.stripeCustomerAccountId;
				this.paymentMethods.set(result.data.paymentMethods);
			} else {
				this.errorMessage.set(result.message || StripeMessagingConstants.STRIPE_ERROR_LOADING_PAYMENT_METHODS());
			}
		} catch {
			this.errorMessage.set(StripeMessagingConstants.STRIPE_ERROR_LOADING_PAYMENT_METHODS());
		} finally {
			this.isLoading.set(false);
		}
	}

	private async loadPreview(): Promise<void> {
		if (!this.charges) return;

		this.isLoadingPreview.set(true);

		const paymentMethodType = this.selectedSavedMethod
			? this.selectedSavedMethod.paymentMethodType
			: this.oneTimePaymentMethodType;

		const result = await this.paymentService.getPaymentPreview({
			organizationId: this.organizationId(),
			paymentMethodType,
			charges: this.charges
		});

		if (result.success && result.data) {
			this.preview.set(result.data);
		} else {
			this.errorMessage.set(result.message || PaymentMessagingConstants.PAYMENT_ERROR_LOADING_PREVIEW());
			this.errorContext = 'preview';
			this.currentStep.set(ModalStep.Error);
		}

		this.isLoadingPreview.set(false);
	}

	private async confirmSavedMethodPayment(): Promise<void> {
		if (!this.selectedSavedMethod || !this.charges) return;

		this.currentStep.set(ModalStep.Processing);

		const intentResult = await this.paymentService.createPaymentIntent({
			organizationId: this.organizationId(),
			stripeCustomerAccountId: this.stripeCustomerAccountId,
			stripePaymentMethodId: this.selectedSavedMethod.id,
			paymentMethodType: this.selectedSavedMethod.paymentMethodType,
			charges: this.charges
		});

		if (!intentResult.success || !intentResult.data) {
			this.errorMessage.set(intentResult.message || PaymentMessagingConstants.PAYMENT_ERROR_CREATING_INTENT());
			this.errorContext = 'confirm';
			this.currentStep.set(ModalStep.Error);
			return;
		}

		const stripe = await this.stripeService.getStripeInstance();
		if (!stripe) {
			this.errorMessage.set(PaymentMessagingConstants.PAYMENT_ERROR_CONFIRMING());
			this.errorContext = 'confirm';
			this.currentStep.set(ModalStep.Error);
			return;
		}

		const { error, paymentIntent } = await stripe.confirmPayment({
			clientSecret: intentResult.data.clientSecret!,
			confirmParams: {
				payment_method: this.selectedSavedMethod.id
			},
			redirect: 'if_required'
		});

		if (error) {
			this.errorMessage.set(error.message || PaymentMessagingConstants.PAYMENT_ERROR_CONFIRMING());
			this.errorContext = 'confirm';
			this.currentStep.set(ModalStep.Error);
			return;
		}

		this.paymentIntent.set({
			...intentResult.data,
			status: this.mapStripeStatus(paymentIntent?.status)
		});
		this.currentStep.set(ModalStep.Confirmation);
	}

	private async confirmOneTimeCardPayment(): Promise<void> {
		if (!this.elements || !this.charges) return;

		this.currentStep.set(ModalStep.Processing);

		const intentResult = await this.paymentService.createPaymentIntent({
			organizationId: this.organizationId(),
			paymentMethodType: PaymentMethodType.Card,
			charges: this.charges
		});

		if (!intentResult.success || !intentResult.data?.clientSecret) {
			this.errorMessage.set(intentResult.message || PaymentMessagingConstants.PAYMENT_ERROR_CREATING_INTENT());
			this.errorContext = 'confirm';
			this.currentStep.set(ModalStep.Error);
			return;
		}

		const stripe = await this.stripeService.getStripeInstance();
		if (!stripe) {
			this.errorMessage.set(PaymentMessagingConstants.PAYMENT_ERROR_CONFIRMING());
			this.errorContext = 'confirm';
			this.currentStep.set(ModalStep.Error);
			return;
		}

		const { error, paymentIntent } = await stripe.confirmPayment({
			elements: this.elements,
			clientSecret: intentResult.data.clientSecret,
			redirect: 'if_required'
		});

		if (error) {
			this.errorMessage.set(error.message || PaymentMessagingConstants.PAYMENT_ERROR_CONFIRMING());
			this.errorContext = 'confirm';
			this.currentStep.set(ModalStep.Error);
			return;
		}

		this.paymentIntent.set({
			...intentResult.data,
			status: this.mapStripeStatus(paymentIntent?.status)
		});
		this.cleanupStripeElements();
		this.currentStep.set(ModalStep.Confirmation);
	}

	private async mountDeferredPaymentElement(): Promise<void> {
		this.cleanupStripeElements();

		const elementsResult = await this.stripeService.createDeferredElement({
			mode: 'payment',
			amount: this.deferredAmount,
			currency: this.deferredCurrencyCode,
			paymentMethodTypes: ['card']
		});

		if (!elementsResult.success || !elementsResult.data) {
			this.errorMessage.set(PaymentMessagingConstants.PAYMENT_ERROR_CREATING_INTENT());
			this.currentStep.set(ModalStep.Error);
			return;
		}

		this.elements = elementsResult.data;

		setTimeout(() => {
			this.mountPaymentElement();
		}, 0);
	}

	private mountPaymentElement(): void {
		if (!this.elements || !this.paymentElementContainer) return;

		this.paymentElement = this.elements.create('payment', {
			paymentMethodOrder: ['card'],
			wallets: { link: 'never' }
		});
		this.paymentElement.on('ready', () => {
			this.isPaymentElementReady.set(true);
		});
		this.paymentElement.mount(this.paymentElementContainer.nativeElement);
	}

	private cleanupStripeElements(): void {
		if (this.paymentElement) {
			this.paymentElement.destroy();
			this.paymentElement = null;
		}
		this.elements = null;
		this.isPaymentElementReady.set(false);
	}

	private goBackFromError(): void {
		this.errorMessage.set('');

		switch (this.errorContext) {
			case 'preview':
				this.currentStep.set(ModalStep.SelectMethod);
				return;

			case 'confirm':
				this.currentStep.set(ModalStep.Preview);
				return;
		}
	}

	private close(): void {
		this.cleanupStripeElements();
		this.bootstrapService.manageModal(`#${this.MODAL_ID}`, ModalActions.Hide);
		this.dirtyFormRegistry.unregister(this);
	}

	private mapStripeStatus(status?: string): PaymentStatus {
		switch (status) {
			case 'succeeded': return PaymentStatus.Succeeded;
			case 'canceled': return PaymentStatus.Canceled;
			case 'requires_payment_method':
			case 'requires_confirmation':
			case 'requires_action': return PaymentStatus.Failed;
			default: return PaymentStatus.Pending;
		}
	}

	// #endregion
}
